<template>
  <button
    class="light-dark-toggle"
    type="button"
    :aria-label="isDark ? 'Switch to light mode' : 'Switch to dark mode'"
    :title="isDark ? 'Light mode' : 'Dark mode'"
    :disabled="isAnimating"
    @click="handleToggle"
  >
    <img
      class="light-dark-toggle__icon"
      :src="currentFrameSrc"
      :style="iconStyle"
      alt=""
      aria-hidden="true"
    >
  </button>
</template>

<script setup>
import { useTheme } from '@/composables/useTheme';
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import frame0000 from '@/assets/images/animations/lightdark/frame_0000.png';
import frame0001 from '@/assets/images/animations/lightdark/frame_0001.png';
import frame0002 from '@/assets/images/animations/lightdark/frame_0002.png';
import frame0003 from '@/assets/images/animations/lightdark/frame_0003.png';
import frame0004 from '@/assets/images/animations/lightdark/frame_0004.png';
import frame0005 from '@/assets/images/animations/lightdark/frame_0005.png';
import frame0006 from '@/assets/images/animations/lightdark/frame_0006.png';
import frame0007 from '@/assets/images/animations/lightdark/frame_0007.png';
import frame0008 from '@/assets/images/animations/lightdark/frame_0008.png';
import frame0009 from '@/assets/images/animations/lightdark/frame_0009.png';
import frame0010 from '@/assets/images/animations/lightdark/frame_0010.png';
import frame0011 from '@/assets/images/animations/lightdark/frame_0011.png';

const { isDark, toggleDarkMode } = useTheme();

const frameSources = [
  frame0000,
  frame0001,
  frame0002,
  frame0003,
  frame0004,
  frame0005,
  frame0006,
  frame0007,
  frame0008,
  frame0009,
  frame0010,
  frame0011,
];

const FRAME_DELAY_MS = 45;
const firstFrameIndex = 0;
const lastFrameIndex = frameSources.length - 1;

const isAnimating = ref(false);
const currentFrameIndex = ref(isDark.value ? firstFrameIndex : lastFrameIndex);
const currentFrameSrc = computed(() => frameSources[currentFrameIndex.value]);
const iconStyle = computed(() => {
  const progress = lastFrameIndex === 0 ? 0 : currentFrameIndex.value / lastFrameIndex;
  const invertAmount = Math.max(0, Math.min(1, 1 - progress));
  return {
    filter: `brightness(0) invert(${invertAmount})`,
  };
});

watch(
  isDark,
  (nextIsDark) => {
    if (isAnimating.value) {
      return;
    }
    currentFrameIndex.value = nextIsDark ? firstFrameIndex : lastFrameIndex;
  },
  { immediate: true }
);

let isUnmounted = false;
onBeforeUnmount(() => {
  isUnmounted = true;
});

const wait = (ms) => new Promise((resolve) => {
  window.setTimeout(resolve, ms);
});

const handleToggle = async () => {
  if (isAnimating.value) {
    return;
  }

  isAnimating.value = true;

  const targetFrameIndex = isDark.value ? lastFrameIndex : firstFrameIndex;
  const direction = currentFrameIndex.value <= targetFrameIndex ? 1 : -1;

  while (currentFrameIndex.value !== targetFrameIndex) {
    currentFrameIndex.value += direction;
    await wait(FRAME_DELAY_MS);
    if (isUnmounted) {
      return;
    }
  }

  toggleDarkMode();
  isAnimating.value = false;
};
</script>

<style scoped>
.light-dark-toggle {
  position: fixed;
  left: 1rem;
  bottom: 1rem;
  width: auto;
  height: auto;
  padding: 0.2rem;
  border-radius: 0 !important;
  border: none !important;
  background-color: transparent !important;
  color: var(--text-color);
  box-shadow: none !important;
  backdrop-filter: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.3s ease, color 0.2s ease, box-shadow 0.3s ease,
    border-color 0.3s ease, backdrop-filter 0.3s ease;
  z-index: 1500;
  appearance: none;
  -webkit-tap-highlight-color: transparent;
  outline: none;
}

.light-dark-toggle:disabled {
  cursor: default;
}

.light-dark-toggle:hover,
.light-dark-toggle:focus-visible {
  color: var(--accent-color);
  box-shadow: none !important;
  outline: none !important;
}

.light-dark-toggle:focus {
  outline: none !important;
  box-shadow: none !important;
}

.light-dark-toggle:active {
  background-color: transparent !important;
  box-shadow: none !important;
  outline: none !important;
}

.light-dark-toggle__icon {
  width: 30px;
  height: 30px;
  display: block;
  object-fit: contain;
  opacity: 0.95;
  transition: filter 45ms linear;
}

</style>
